import { useEffect, useMemo } from "react";
import {
  useController,
  useWatch,
  type Control,
  type FieldPath,
  type FieldValues,
  type UseFormResetField,
  type UseFormSetFocus,
  type UseFormSetValue,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Autocomplete,
  type IAutocompleteProps,
} from "@components/inputs/Autocomplete/Autocomplete";
import { Input, type IInputProps } from "@components/inputs/Input/Input";
import {
  useMunicipalitiesQuery,
  useSettlementsQuery,
  useStreetsQuery,
} from "@services/adresses/queries";

interface IFormAddressInputChildrenProps {
  MunicipalityField: React.FC<Partial<IAutocompleteProps>>;
  SettlementField: React.FC<Partial<IAutocompleteProps>>;
  StreetField: React.FC<Partial<IAutocompleteProps>>;
  HouseField: React.FC<Partial<IInputProps>>;
  ApartmentField: React.FC<Partial<IInputProps>>;
}

type TFormAddressInputProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  municipalityFieldName: TName;
  settlementFieldName: TName;
  streetFieldName: TName;
  houseFieldName: TName;
  apartmentFieldName: TName;
  control?: Control<TFieldValues>;
  resetField: UseFormResetField<TFieldValues>;
  setFocus: UseFormSetFocus<TFieldValues>;
  setValue: UseFormSetValue<TFieldValues>;
  children?: (props: IFormAddressInputChildrenProps) => JSX.Element;
  countryCode?: string;
  disabled?: boolean;
};

const InputFieldComponent = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  control,
  disabled,
  ...restProps
}: IInputProps & { name: TName; control?: Control<TFieldValues> }) => {
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
  });
  return (
    <Input
      {...restProps}
      {...field}
      disabled={disabled}
      error={error?.message}
    />
  );
};

const AutocompleteFieldComponent = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  control,
  disabled,
  ...restProps
}: IAutocompleteProps & { name: TName; control?: Control<TFieldValues> }) => {
  const {
    field: { ref, ...restField },
    fieldState: { error },
  } = useController({
    name,
    control,
  });

  return (
    <Autocomplete
      {...restProps}
      {...restField}
      disabled={disabled}
      inputRef={ref}
      error={error?.message}
      onChange={(e) => {
        restField.onChange(e);
        restProps.onChange?.(e);
      }}
    />
  );
};

export const FormAddressInput = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  municipalityFieldName,
  settlementFieldName,
  streetFieldName,
  houseFieldName,
  apartmentFieldName,
  control,
  resetField,
  setFocus,
  setValue,
  children,
  countryCode,
  disabled,
}: TFormAddressInputProps<TFieldValues, TName>) => {
  const { t } = useTranslation();

  const [municipalityFieldValue, settlementFieldValue] = useWatch({
    name: [municipalityFieldName, settlementFieldName],
    control,
  });

  const { data: municipalities, isFetching: isMunicipalitiesFetching } =
    useMunicipalitiesQuery(countryCode);

  const municipalitiesOptions = useMemo(
    () =>
      municipalities?.map((municipality) => ({
        value: municipality.municipalityId,
        label: municipality.name,
      })),
    [municipalities],
  );

  const { data: settlements, isFetching: isSettlementsFetching } =
    useSettlementsQuery(
      municipalityFieldValue ?? 0,
      !!municipalityFieldValue,
      countryCode,
    );

  const settlementOptions = useMemo(
    () =>
      settlements?.map((settlement) => ({
        value: settlement.settlementId,
        label: settlement.name,
      })),
    [settlements],
  );

  const { data: streets, isFetching: isStreetsFetching } = useStreetsQuery(
    municipalityFieldValue ?? 0,
    settlementFieldValue ?? 0,
    !!settlementFieldValue,
    countryCode,
  );

  const streetOptions = useMemo(
    () =>
      streets?.map((street) => ({
        value: street.streetId,
        label: street.name,
      })),
    [streets],
  );

  useEffect(() => {
    if (!isSettlementsFetching) {
      setFocus(settlementFieldName);
    }
  }, [setFocus, settlementFieldName, settlements, isSettlementsFetching]);

  useEffect(() => {
    if (!isSettlementsFetching) {
      setFocus(streetFieldName);
    }
  }, [
    setFocus,
    streetFieldName,
    streets,
    isStreetsFetching,
    isSettlementsFetching,
  ]);

  useEffect(() => {
    if (settlements?.length === 1) {
      // @ts-expect-error - c
      setValue(settlementFieldName, settlements?.[0]?.settlementId);
    }
  }, [setValue, settlementFieldName, settlements]);

  useEffect(() => {
    if (streets?.length === 1) {
      // @ts-expect-error - c
      setValue(streetFieldName, streets?.[0]?.streetId);
    }
  }, [setValue, streetFieldName, streets]);

  const MunicipalityField: React.FC<Partial<IAutocompleteProps>> = useMemo(
    () => (props: Partial<IAutocompleteProps>) => (
      <AutocompleteFieldComponent
        label={t("formAddressInput.fields.municipality.label")}
        {...props}
        name={municipalityFieldName}
        control={control}
        options={municipalitiesOptions ?? []}
        onChange={() => {
          resetField(settlementFieldName);
          resetField(streetFieldName);
        }}
        isLoading={isMunicipalitiesFetching}
      />
    ),
    [
      control,
      isMunicipalitiesFetching,
      municipalitiesOptions,
      municipalityFieldName,
      resetField,
      settlementFieldName,
      streetFieldName,
      t,
    ],
  );

  const SettlementField: React.FC<Partial<IAutocompleteProps>> = useMemo(
    () => (props: Partial<IAutocompleteProps>) => (
      <AutocompleteFieldComponent
        label={t("formAddressInput.fields.settlement.label")}
        {...props}
        name={settlementFieldName}
        control={control}
        options={settlementOptions ?? []}
        onChange={() => {
          resetField(streetFieldName);
        }}
        disabled={!municipalityFieldValue || props.disabled}
        isLoading={isSettlementsFetching}
      />
    ),
    [
      control,
      isSettlementsFetching,
      municipalityFieldValue,
      resetField,
      settlementFieldName,
      settlementOptions,
      streetFieldName,
      t,
    ],
  );

  const StreetField: React.FC<Partial<IAutocompleteProps>> = useMemo(
    () => (props: Partial<IAutocompleteProps>) => (
      <AutocompleteFieldComponent
        label={t("formAddressInput.fields.street.label")}
        {...props}
        name={streetFieldName}
        control={control}
        options={streetOptions ?? []}
        disabled={!settlementFieldValue || props.disabled}
        isLoading={isStreetsFetching}
      />
    ),
    [
      control,
      isStreetsFetching,
      streetOptions,
      t,
      settlementFieldValue,
      streetFieldName,
    ],
  );

  const HouseField: React.FC<Partial<IInputProps>> = useMemo(
    () => (props: Partial<IInputProps>) => (
      <InputFieldComponent
        label={t("formAddressInput.fields.house.label")}
        {...props}
        name={houseFieldName}
        control={control}
      />
    ),
    [control, houseFieldName, t],
  );

  const ApartmentField: React.FC<Partial<IInputProps>> = useMemo(
    () => (props: Partial<IInputProps>) => (
      <InputFieldComponent
        label={t("formAddressInput.fields.apartment.label")}
        {...props}
        name={apartmentFieldName}
        control={control}
      />
    ),
    [control, apartmentFieldName, t],
  );

  return children ? (
    children?.({
      MunicipalityField,
      SettlementField,
      StreetField,
      HouseField,
      ApartmentField,
    })
  ) : (
    <div className="flex flex-col gap-4">
      <MunicipalityField disabled={disabled} />
      <SettlementField disabled={disabled} />
      <div className="flex flex-col gap-4 sm:flex-row">
        <div className="flex basis-1/2">
          <StreetField className="w-full" disabled={disabled} />
        </div>
        <div className="flex basis-1/2 gap-4">
          <HouseField disabled={disabled} />
          <ApartmentField disabled={disabled} />
        </div>
      </div>
    </div>
  );
};
